import type { HeatmapAxis, HeatmapCell } from '../../charts/HeatmapChart'
import type { CreditValueBucket, ProgramRateComparison, StatusCountBucket } from '../../../../types/insights'

/**
 * IB-282 View 3 — pure, DB-free/fetch-free mapping helpers (Vitest-friendly by construction, same
 * posture as `screens/cases/analytics/analyticsMappers.ts`). Every mapper degrades each source
 * independently and never fabricates a value the backend didn't actually resolve.
 */

export interface JurisdictionHeatmapGrid {
  rows: HeatmapAxis[]
  columns: HeatmapAxis[]
  cells: HeatmapCell[]
}

/**
 * AC3.1 — reshapes the EXISTING `credit-value-by-jurisdiction-fy` payload (`CreditValueBucket[]`)
 * into a state × FY grid. `fiscalYearId === null` buckets (AC25's own "no filing yet" zero-fill row)
 * contribute their jurisdiction to the row axis but are never turned into a column — a jurisdiction
 * with only that bucket still renders as a full row of zero-fill cells across every REAL fiscal
 * year column, never dropped from the axis.
 */
export function buildJurisdictionHeatmapGrid(buckets: CreditValueBucket[]): JurisdictionHeatmapGrid {
  const jurisdictions = new Set<string>()
  const fiscalYears = new Set<string>()
  const amountByKey = new Map<string, number>()

  for (const b of buckets) {
    jurisdictions.add(b.jurisdiction)
    if (b.fiscalYearId) {
      fiscalYears.add(b.fiscalYearId)
      amountByKey.set(`${b.jurisdiction}::${b.fiscalYearId}`, Number(b.sumCertifiedAmount))
    }
  }

  const rows: HeatmapAxis[] = [...jurisdictions].sort((a, b) => a.localeCompare(b)).map((j) => ({ key: j, label: j }))
  const columns: HeatmapAxis[] = [...fiscalYears].sort((a, b) => a.localeCompare(b)).map((fy) => ({ key: fy, label: fy }))

  const cells: HeatmapCell[] = []
  for (const r of rows) {
    for (const c of columns) {
      cells.push({ rowKey: r.key, colKey: c.key, value: amountByKey.get(`${r.key}::${c.key}`) ?? 0 })
    }
  }
  return { rows, columns, cells }
}

export interface ProgramRateRow {
  programId: string
  code: string
  /** A fraction (e.g. `0.20` for 20%) — `formatRatePercent` converts to a display percent. */
  baseRate: number
  /** `NaN` sentinel for "no uplift authored" (AC3.2) — never a fabricated `0`; `formatRatePercent` renders it as an explicit "No uplift" label. */
  upliftRate: number
  hasUplift: boolean
}

/**
 * AC3.2 — pairs each program's authored base/uplift rate for `BarChartWrapper`'s grouped-bar
 * series. A row with no `baseRate` at all (the backend's own "no effective RuleSet/Parameter as-of
 * this date" omission) is filtered out entirely, never rendered as a $0 bar.
 */
export function buildProgramRateRows(comparisons: ProgramRateComparison[]): ProgramRateRow[] {
  return comparisons
    .filter((c): c is ProgramRateComparison & { baseRate: string } => c.baseRate !== null)
    .map((c) => ({
      programId: c.programId,
      code: c.code,
      baseRate: Number(c.baseRate),
      upliftRate: c.hasUplift && c.upliftRate !== null ? Number(c.upliftRate) : Number.NaN,
      hasUplift: c.hasUplift,
    }))
}

/** AC3.4 — total case/filing count across a `filings-by-status` payload, for the R&D-vs-Film KPI row. */
export function sumStatusCounts(buckets: StatusCountBucket[]): number {
  return buckets.reduce((total, b) => total + b.count, 0)
}
